import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { schoolApi } from '../api/school';
import { useAuth } from './AuthContext';

const AcademicYearContext = createContext(null);

export const AcademicYearProvider = ({ children }) => {
  const { currentOrg, user, token } = useAuth();
  const orgId = currentOrg?.id || user?.organization_id || null;
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchYears = useCallback(async () => {
    if (!orgId || !token) {
      setAcademicYears([]);
      setSelectedYear(null);
      return;
    }
    setLoading(true);
    try {
      const rows = (await schoolApi.listYears(orgId)) || [];
      setAcademicYears(rows);
      const savedId = localStorage.getItem(`selectedAY_${orgId}`);
      const saved = rows.find((y) => y.id === savedId);
      if (saved) {
        setSelectedYear(saved);
      } else {
        const active = rows.find((y) => y.is_active) || rows[0] || null;
        setSelectedYear(active);
        if (active) localStorage.setItem(`selectedAY_${orgId}`, active.id);
      }
    } catch (e) {
      setAcademicYears([]);
      setSelectedYear(null);
    } finally {
      setLoading(false);
    }
  }, [orgId, token]);

  useEffect(() => { fetchYears(); }, [fetchYears]);

  const switchYear = (year) => {
    setSelectedYear(year);
    if (year && orgId) localStorage.setItem(`selectedAY_${orgId}`, year.id);
  };

  return (
    <AcademicYearContext.Provider value={{
      academicYears,
      selectedYear,
      selectedYearId: selectedYear?.id || null,
      loading,
      switchYear,
      fetchYears,
      hasYears: academicYears.length > 0,
    }}>
      {children}
    </AcademicYearContext.Provider>
  );
};

export const useAcademicYear = () => {
  const ctx = useContext(AcademicYearContext);
  if (!ctx) throw new Error('useAcademicYear must be used within an AcademicYearProvider');
  return ctx;
};
